import {
  HUNDREDWEIGHT,
  BY_THE_PALLET,
  BY_THE_PALLET_WAREHOUSE_BUILT,
  BY_THE_UNIT_ITEM,
  BY_THE_VAULT,
  SQUARE_FOOT_ITEM,
  SQUARE_FOOT_LOCATION,
  CUBIC_FOOT_ITEM,
  CUBIC_FOOT_LOCATION,
  STORAGE_LOCATIONS_OCCUPIED,
} from "../../project/settings/utils/billingTypes";
import { formatRackLocationLabel } from "src/utils/rackLocations";

// Configuration for which fields to show based on billing type
// Uses exact billing type names from constants and includes required billing fields
export const BILLING_TYPE_FIELDS = {
  // Pallet billing - requires pallet_type
  [BY_THE_PALLET]: ['data_source', 'quantity', 'billing_type', 'pallet_type', 'rate', 'rack_locations'],
  [BY_THE_PALLET_WAREHOUSE_BUILT]: ['data_source', 'quantity', 'billing_type', 'pallet_type', 'rate', 'rack_locations'],

  // Square footage billing - requires dimensions AND square_footage
  [SQUARE_FOOT_ITEM]: ['data_source', 'quantity', 'billing_type', 'dimensions', 'square_footage', 'rate'],
  [SQUARE_FOOT_LOCATION]: ['data_source', 'quantity', 'billing_type', 'dimensions', 'square_footage', 'rate'],

  // Cubic footage billing - requires dimensions AND cubic_footage
  [CUBIC_FOOT_ITEM]: ['data_source', 'quantity', 'billing_type', 'dimensions', 'cubic_footage', 'rate'],
  [CUBIC_FOOT_LOCATION]: ['data_source', 'quantity', 'billing_type', 'dimensions', 'cubic_footage', 'rate'],

  // Hundredweight billing - requires weight only
  [HUNDREDWEIGHT]: ['data_source', 'quantity', 'billing_type', 'weight', 'rate', 'rack_locations'],

  // Location-based billing
  [STORAGE_LOCATIONS_OCCUPIED]: ['data_source', 'quantity', 'billing_type', 'rate', 'rack_locations'],

  // Unit/Item billing
  [BY_THE_UNIT_ITEM]: ['data_source', 'quantity', 'billing_type', 'unit_type', 'rate'],
  [BY_THE_VAULT]: ['data_source', 'quantity', 'billing_type', 'rate'],
};

// Formulas for each billing type (used in edit view)
export const BILLING_TYPE_FORMULAS = {
  [BY_THE_PALLET]: "Pallet type × number of storage locations × rate",
  [BY_THE_PALLET_WAREHOUSE_BUILT]: "Pallet type × number of storage locations × rate",
  [SQUARE_FOOT_ITEM]: "Square footage × rate per sq ft",
  [SQUARE_FOOT_LOCATION]: "Square footage × rate per sq ft",
  [CUBIC_FOOT_ITEM]: "Cubic footage × rate per cu ft",
  [CUBIC_FOOT_LOCATION]: "Cubic footage × rate per cu ft",
  [HUNDREDWEIGHT]: "Weight ÷ 100 × rate per cwt",
  [STORAGE_LOCATIONS_OCCUPIED]: "Number of locations × rate per location",
  [BY_THE_UNIT_ITEM]: "Number of units × rate per unit",
  [BY_THE_VAULT]: "Number of vaults × rate per vault",
};

// Map pallet type to rate key
export function getPalletRateKey(palletType) {
  switch (palletType) {
    case 'Standard Pallet':
      return 'normal_pallet_rate';
    case 'Oversized Pallet':
      return 'oversized_pallet_rate';
    case 'Double Oversized Pallet':
      return 'double_oversized_pallet_rate';
    default:
      return 'normal_pallet_rate';
  }
}

export function getUnitRateKey(unitType) {
  return `${unitType || 'standard_unit'}_rate`;
}

// Rate unit labels for display (e.g. "cwt" is the industry standard abbreviation)
const BILLING_TYPE_RATE_UNITS = {
  [HUNDREDWEIGHT]: 'cwt',
  [BY_THE_PALLET]: 'pallet',
  [BY_THE_PALLET_WAREHOUSE_BUILT]: 'pallet',
  [SQUARE_FOOT_ITEM]: 'sq ft',
  [SQUARE_FOOT_LOCATION]: 'sq ft',
  [CUBIC_FOOT_ITEM]: 'cu ft',
  [CUBIC_FOOT_LOCATION]: 'cu ft',
  [STORAGE_LOCATIONS_OCCUPIED]: 'location',
  [BY_THE_UNIT_ITEM]: 'unit',
  [BY_THE_VAULT]: 'vault',
};

export function getRateUnit(billingType) {
  return BILLING_TYPE_RATE_UNITS[billingType] || '';
}

export function getBillingTypeFields(billingType) {
  return BILLING_TYPE_FIELDS[billingType] || ['data_source', 'billing_type', 'rate'];
}

export function formatRackLocations(rackLocations) {
  if (!rackLocations || rackLocations.length === 0) {
    return '';
  }
  
  return rackLocations
    .map(rl => formatRackLocationLabel(rl, { includeQty: !!rl.sub_qty && rackLocations.length > 1 }))
    .join('\n');
}
